import { useState, useMemo, MouseEvent, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  AlertColor,
  Backdrop,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  TableSortLabel,
  TextField,
  Toolbar,
} from "@mui/material";
import { DateTime } from "luxon";
import { Order, stableSort, getComparator } from "utils/Table";
import { IWad, useWadService, WadState } from "services/registry/WadsService";
import AddIcon from "@mui/icons-material/Add";
import { visuallyHidden } from "@mui/utils";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SimpleListDialog from "components/SimpleListDialog";
import ChipsArray from "components/ChipsArray";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import { Notify } from "components/Notify";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { IPackage } from "services/registry/PackageService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { downloadFile, fileNameFromContentDisposition } from "utils/Download";

interface HeadCell {
  id: keyof IWad | string;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
    padding: "none",
  },
  {
    id: "description",
    label: "Description",
  },
  {
    id: "labels",
    label: "Labels",
  },
  {
    id: "files",
    label: "Files",
  },
  {
    id: "state",
    label: "State",
  },
  {
    id: "createdTime",
    label: "Creation Date",
  },
  {
    id: "publishedTime",
    label: "Published Date",
  },
  {
    id: "bucketPath",
    label: "S3 Bucket",
  },
  {
    id: "actions",
    label: "",
  },
];

interface WadsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: keyof IWad | string) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const WadsTableHead = ({ order, orderBy, onRequestSort }: WadsTableHeadProps) => {
  const createSortHandler = (property: keyof IWad | string) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
interface WadsTableToolbarProps {
  onAddButtonClick: () => void;
  filterQuery: string;
  setFilterQuery: (value: string) => void;
  onRefreshButtonClick: () => void;
}

const WadsTableToolbar = ({
  onAddButtonClick,
  filterQuery,
  setFilterQuery,
  onRefreshButtonClick,
}: WadsTableToolbarProps) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddButtonClick}>
        Add
      </Button>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick} style={{ marginLeft: 8 }}>
        Refresh
      </Button>
      <TextField
        id="outlined-search"
        label="Search wads"
        type="search"
        size="small"
        onChange={(e) => setFilterQuery(e.target.value)}
        sx={{ width: "360px", marginLeft: 1 }}
        value={filterQuery}
      />
    </Toolbar>
  );
};

export interface WadsTableProps {
  rows: IWad[];
  selectedItem: IWad | undefined;
  setSelectedItem: React.Dispatch<React.SetStateAction<IWad | undefined>>;
  onAddButtonClick: () => Promise<void>;
  onEditButtonClick: () => void;
  onRemoveButtonClick: (option: boolean) => void;
  onPublishButtonClick: (option: boolean) => void;
  onDuplicateButtonClick: (wadId: string) => void;
  onViewButtonClick: (wadId: string) => void;
  packages: IPackage[];
  initialFilterQuery?: string;
  onRefreshButtonClick: () => void;
}

export const WadsTable = ({
  rows,
  selectedItem,
  setSelectedItem,
  onAddButtonClick,
  onEditButtonClick,
  onRemoveButtonClick,
  onPublishButtonClick,
  onDuplicateButtonClick,
  packages,
  onViewButtonClick,
  initialFilterQuery = "",
  onRefreshButtonClick,
}: WadsTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<keyof IWad | string>("createdTime");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openLabelDialog, setOpenLabelDialog] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [filterQuery, setFilterQuery] = useState<string>(initialFilterQuery);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<IWad | null>(null);
  const wadService = useWadService();

  useEffect(() => {
    setFilterQuery(initialFilterQuery);
  }, [initialFilterQuery]);

  const getWadDownloadUrl = (wad: IWad): string | undefined => {
    if (!wad.bucketPath || !wad.id) {
      return undefined;
    }
    // Extract bucket name (part before first slash)
    const bucketName = wad.bucketPath.split("/")[0];
    // Extract path (everything after bucket name, including trailing slash)
    const path = wad.bucketPath.substring(bucketName.length + 1);
    return `https://${bucketName}.s3.us-east-1.amazonaws.com/${path}`;
  };

  const getFileDownloadUrl = (wad: IWad, fileName: string): string | undefined => {
    const wadDownloadUrl = getWadDownloadUrl(wad);
    if (!wadDownloadUrl) {
      return undefined;
    }
    const baseUrl = wadDownloadUrl.substring(0, wadDownloadUrl.length - 5);
    return `${baseUrl}/${fileName}`;
  };

  const requestSortHandler = (event: MouseEvent<unknown>, property: keyof IWad | string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showWadFiles = (row: IWad) => {
    setSelectedItem(row);
    setOpenLabelDialog(true);
  };

  const handleCloseLabelDialog = () => {
    setOpenLabelDialog(false);
  };

  const handleEditClick = (event: React.MouseEvent<unknown>, row: IWad) => {
    setSelectedItem(row);
    onEditButtonClick();
    handleCloseMenu();
  };

  const handleRemoveClick = (row: IWad) => {
    setSelectedItem(row);
    onRemoveButtonClick(true);
    handleCloseMenu();
  };

  const handlePublishClick = (row: IWad) => {
    setSelectedItem(row);
    onPublishButtonClick(true);
    handleCloseMenu();
  };

  const handleDuplicateClick = (row: IWad) => {
    setSelectedItem(row);
    onDuplicateButtonClick(row.id);
    handleCloseMenu();
  };

  const handleViewClick = (row: IWad) => {
    setSelectedItem(row);
    onViewButtonClick(row.id);
    handleCloseMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: IWad) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleDownloadFile = (fileName: string): void => {
    if (!selectedItem) {
      return;
    }
    const url = getFileDownloadUrl(selectedItem, fileName);
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleDownloadAllFiles = async (): Promise<void> => {
    if (!selectedItem || !selectedItem.files || selectedItem.files.length === 0) {
      return;
    }

    setLoading(true);
    try {
      const res = await wadService.downloadAsZip(selectedItem.id);
      const contentDispositionHeader = res.headers.get("content-disposition");
      let fileName = `wad-${selectedItem.id}-files.zip`;
      if (contentDispositionHeader) {
        const contentDispositionFileName = fileNameFromContentDisposition(contentDispositionHeader);
        fileName = contentDispositionFileName ? contentDispositionFileName : fileName;
      }

      const content = await res.blob();
      downloadFile(content, fileName);
    } catch (e: unknown) {
      setAlertMessage((e as Error).message || "Failed to download files.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  async function handleRemoveFile(fileOriginalName: string): Promise<void> {
    if (!fileOriginalName || !selectedItem) {
      return;
    }
    setLoading(true);
    try {
      await wadService.removeFile(selectedItem.id, fileOriginalName);
      selectedItem.files = selectedItem.files.filter((f) => f.name !== fileOriginalName);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to remove the File.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  const filteredRows = useMemo(() => {
    const query = filterQuery.toLowerCase();

    return rows.filter((row) => {
      const matchesId = row.id.toLowerCase().includes(query);
      const matchesDescription = row.description?.toLowerCase().includes(query);
      const matchesLabels = row.labels.some((label) => label.toLowerCase().includes(query));
      const matchesFiles = row.files.some((file) => file.name.toLowerCase().includes(query));
      const matchesPackages = packages.some(
        (pkg) =>
          pkg.id.toLowerCase().includes(query) && pkg.wads.some((wad) => wad?.id?.toLowerCase() === row.id.toLowerCase()),
      );

      return matchesId || matchesDescription || matchesLabels || matchesFiles || matchesPackages;
    });
  }, [filterQuery, rows, packages]);
  const visibleRows = useMemo(() => {
    return stableSort(filteredRows, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  const isSelected = (row: IWad, selectedItem: IWad | undefined) => selectedItem !== undefined && row.id === selectedItem.id;

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <WadsTableToolbar
            onAddButtonClick={onAddButtonClick}
            filterQuery={filterQuery}
            setFilterQuery={setFilterQuery}
            onRefreshButtonClick={onRefreshButtonClick}
          />
          <TableContainer>
            <Table sx={{ minWidth: 750 }} size="small">
              <WadsTableHead order={order} orderBy={orderBy} onRequestSort={requestSortHandler} rowCount={rows.length} />
              <TableBody>
                {visibleRows.map((row, index) => {
                  const labelId = `wads-table-checkbox-${index}`;
                  const creationDate = DateTime.fromSeconds(row.createdTime).toLocaleString(
                    DateTime.DATETIME_SHORT_WITH_SECONDS,
                  );
                  const publishedDate = row.publishedTime
                    ? DateTime.fromSeconds(row.publishedTime).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                    : "";

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      onClick={() => setSelectedItem(row)}
                      tabIndex={-1}
                      key={row.id}
                      selected={isSelected(row, selectedItem)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="normal" />
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        <Link href={getWadDownloadUrl(row)} target="_blank" rel="noopener noreferrer">
                          {row.id}
                        </Link>
                      </TableCell>
                      <TableCell align="left">
                        {row.description && row.description.length > 30
                          ? `${row.description.substring(0, 30)}...`
                          : row.description}
                      </TableCell>
                      <TableCell align="left">
                        <ChipsArray
                          data={[...row.labels]}
                          setChipData={() => {
                            return;
                          }}
                          showDelete={false}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <IconButton
                          onClick={(e) => {
                            showWadFiles(row);
                            e.stopPropagation();
                          }}
                        >
                          <Badge badgeContent={row.files.length} color="primary" showZero>
                            <ListAltIcon fontSize="inherit" />
                          </Badge>
                        </IconButton>
                      </TableCell>
                      <TableCell align="left">{row.state}</TableCell>
                      <TableCell align="left">{creationDate}</TableCell>
                      <TableCell align="left">{publishedDate}</TableCell>
                      <TableCell align="left">{row.bucketPath?.substring(0, row.bucketPath.lastIndexOf("/") + 1)}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={(event) => handleOpenMenu(event, row)} size="small">
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 33 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={pageChangeHandler}
            onRowsPerPageChange={rowsPerPageChangeHandler}
          />
        </Paper>
      </Box>
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={(event) => menuRow && handleEditClick(event, menuRow)}
          disabled={menuRow?.state === WadState.PUBLISHED}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleViewClick(menuRow)}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Content</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleDuplicateClick(menuRow)}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handlePublishClick(menuRow)} disabled={menuRow?.state === WadState.PUBLISHED}>
          <ListItemIcon>
            <PublishIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Publish</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuRow && handleRemoveClick(menuRow)} disabled={menuRow?.state === WadState.PUBLISHED}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>
      <SimpleListDialog
        title="Files"
        listItems={selectedItem?.files.map((f) => f.name)}
        emptyListMessage="No files to display"
        open={openLabelDialog}
        onClose={handleCloseLabelDialog}
        showDeleteButton={selectedItem?.state !== WadState.PUBLISHED}
        onDelete={(item) => handleRemoveFile(item)}
        onSelect={(item) => handleDownloadFile(item)}
        actions={
          selectedItem?.files && selectedItem.files.length > 0 ? (
            <Button startIcon={<DownloadIcon />} onClick={handleDownloadAllFiles} variant="outlined" size="small">
              Download All as ZIP
            </Button>
          ) : undefined
        }
      />
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
